import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'

export type Tag = Database['public']['Tables']['tags']['Row']
export type TagInsert = Database['public']['Tables']['tags']['Insert']
export type TagUpdate = Database['public']['Tables']['tags']['Update']
export type VideoTag = Database['public']['Tables']['video_tags']['Row']

export class TagService {
  // Get all tags with category information
  static async getTags(includeInactive = false): Promise<Tag[]> {
    let query = supabase
      .from('tags')
      .select(`
        *,
        categories!inner(name)
      `)
      .order('display_order', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  // Get tags for dropdown (simplified)
  static async getTagsForDropdown(): Promise<Array<{ id: string; name: string; description?: string | null }>> {
    const { data, error } = await supabase
      .from('tags')
      .select('id, name, slug, description')
      .eq('is_active', true)
      .order('display_order', { ascending: true })

    if (error) throw error
    
    return (data || []).map(tag => ({
      id: tag.slug,
      name: tag.name,
      description: tag.description
    }))
  }

  // Get tags by category
  static async getTagsByCategory(categoryId: string, includeInactive = false): Promise<Tag[]> {
    let query = supabase
      .from('tags')
      .select(`
        *,
        categories!inner(name)
      `)
      .eq('category_id', categoryId)
      .order('display_order', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  // Get a single tag by ID or slug
  static async getTag(idOrSlug: string): Promise<Tag | null> {
    // Try slug first since most calls will use slug
    const { data: bySlug, error: slugError } = await supabase
      .from('tags')
      .select('*')
      .eq('slug', idOrSlug)
      .single()

    if (!slugError && bySlug) return bySlug

    // If slug doesn't work, try ID
    const { data: byId, error: idError } = await supabase
      .from('tags')
      .select('*')
      .eq('id', idOrSlug)
      .single()

    if (!idError && byId) return byId

    // Return null if neither works
    return null
  }

  // Create a new tag
  static async createTag(tag: TagInsert): Promise<Tag> {
    // Auto-generate display_order if not provided
    if (tag.display_order === undefined) {
      const { data: maxOrder } = await supabase
        .from('tags')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1)
        .single()

      tag.display_order = (maxOrder?.display_order || 0) + 1
    }

    const { data, error } = await supabase
      .from('tags')
      .insert(tag)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update a tag
  static async updateTag(id: string, updates: TagUpdate): Promise<Tag> {
    const { data, error } = await supabase
      .from('tags')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete a tag
  static async deleteTag(id: string): Promise<void> {
    // First remove all video_tags associations
    await supabase
      .from('video_tags')
      .delete()
      .eq('tag_id', id)

    // Then delete the tag
    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Reorder tags
  static async reorderTags(tagOrders: Array<{ id: string; display_order: number }>): Promise<void> {
    const updates = tagOrders.map(({ id, display_order }) =>
      supabase
        .from('tags')
        .update({ display_order, updated_at: new Date().toISOString() })
        .eq('id', id)
    )

    await Promise.all(updates)
  }

  // Get tags for a video
  static async getVideoTags(videoId: string): Promise<Tag[]> {
    const { data, error } = await supabase
      .from('video_tags')
      .select(`
        tags!inner(*)
      `)
      .eq('video_id', videoId)
      .order('tags(name)', { ascending: true })

    if (error) throw error

    return (data || []).map(vt => vt.tags).filter(Boolean).flat() as Tag[]
  }

  // Batch query to get tags for multiple videos at once
  static async getVideoTagsBatch(videoIds: string[]): Promise<Record<string, Tag[]>> {
    if (videoIds.length === 0) return {}

    const { data, error } = await supabase
      .from('video_tags')
      .select(`
        video_id,
        tags!inner(*)
      `)
      .in('video_id', videoIds)
      .order('tags(name)', { ascending: true })

    if (error) throw error

    // Group tags by video_id
    const result: Record<string, Tag[]> = {}
    videoIds.forEach(id => {
      result[id] = []
    })

    if (data) {
      for (const item of data) {
        if (item.video_id && item.tags) {
          if (!result[item.video_id]) {
            result[item.video_id] = []
          }
          // item.tags is a single tag object from the join
          const tag = item.tags as unknown as Tag
          result[item.video_id].push(tag)
        }
      }
    }

    return result
  }

  // Update tags for a video
  static async updateVideoTags(videoId: string, tagIds: string[]): Promise<void> {
    // Remove existing tags
    await supabase
      .from('video_tags')
      .delete()
      .eq('video_id', videoId)

    // Add new tags
    if (tagIds.length > 0) {
      const videoTags = tagIds.map(tagId => ({
        video_id: videoId,
        tag_id: tagId
      }))

      const { error } = await supabase
        .from('video_tags')
        .insert(videoTags)

      if (error) throw error
    }
  }

  // Get tag statistics
  static async getTagStats(): Promise<Array<{ tag: Tag; videoCount: number }>> {
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })

    if (tagsError) throw tagsError

    const { data: videoTags, error: videoTagsError } = await supabase
      .from('video_tags')
      .select('tag_id')

    if (videoTagsError) throw videoTagsError

    const tagCounts = (videoTags || []).reduce((acc, vt) => {
      acc[vt.tag_id] = (acc[vt.tag_id] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return (tags || []).map(tag => ({
      tag,
      videoCount: tagCounts[tag.id] || 0
    }))
  }

  // Search tags by name
  static async searchTags(query: string): Promise<Tag[]> {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .ilike('name', `%${query}%`)
      .eq('is_active', true)
      .order('name', { ascending: true })
      .limit(10)

    if (error) throw error
    return data || []
  }

  // Migrate existing inline tags to tag system
  static async migrateInlineTags(): Promise<{
    createdTags: number
    updatedVideos: number
  }> {
    // Get default category (beginner)
    const { data: defaultCategory, error: categoryError } = await supabase
      .from('categories')
      .select('id')
      .eq('slug', 'beginner')
      .single()
    
    if (categoryError || !defaultCategory) {
      throw new Error('Default category not found')
    }
    
    // Get all videos with tags
    const { data: videos, error: videosError } = await supabase
      .from('videos')
      .select('id, tags')
      .not('tags', 'is', null)

    if (videosError) throw videosError

    // Extract unique tags
    const uniqueTags = new Set<string>()
    videos?.forEach(video => {
      video.tags?.forEach((tag: string) => uniqueTags.add(tag))
    })

    // Get existing tags
    const { data: existingTags, error: existingError } = await supabase
      .from('tags')
      .select('name, id')

    if (existingError) throw existingError

    const existingTagMap = new Map(
      existingTags?.map(tag => [tag.name.toLowerCase(), tag.id]) || []
    )

    // Create new tags
    let createdTags = 0
    const tagNameToId = new Map<string, string>()

    for (const tagName of uniqueTags) {
      const normalizedName = tagName.toLowerCase()
      
      if (!existingTagMap.has(normalizedName)) {
        const slug = tagName
          .toLowerCase()
          .replace(/[^a-z0-9\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim()

        const newTag = await this.createTag({
          name: tagName,
          slug: slug || `tag-${Date.now()}`,
          description: null,
          category_id: defaultCategory.id,
          is_active: true
        })

        tagNameToId.set(normalizedName, newTag.id)
        createdTags++
      } else {
        tagNameToId.set(normalizedName, existingTagMap.get(normalizedName)!)
      }
    }

    // Update video-tag relationships
    let updatedVideos = 0
    for (const video of videos || []) {
      if (video.tags && video.tags.length > 0) {
        const tagIds = video.tags
          .map((tag: any) => tagNameToId.get(tag.toLowerCase()))
          .filter(Boolean) as string[]

        if (tagIds.length > 0) {
          await this.updateVideoTags(video.id, tagIds)
          updatedVideos++
        }
      }
    }

    return { createdTags, updatedVideos }
  }

  // Check if a tag name is unique
  static async isTagNameUnique(name: string, excludeId?: string): Promise<boolean> {
    let query = supabase
      .from('tags')
      .select('id')
      .ilike('name', name)

    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data, error } = await query

    if (error) throw error
    return !data || data.length === 0
  }

  // Generate unique slug from name
  static async generateUniqueSlug(name: string): Promise<string> {
    const baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim() || 'tag'

    let slug = baseSlug
    let counter = 1

    while (true) {
      const { data } = await supabase
        .from('tags')
        .select('id')
        .eq('slug', slug)
        .single()

      if (!data) return slug

      slug = `${baseSlug}-${counter}`
      counter++
    }
  }
}